import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '../App.css';
import { DeckIntelligenceSections } from '../components/DeckIntelligenceSections';
import GlobalEmptyState from '../components/GlobalEmptyState';
import GlobalSidebar from '../components/GlobalSidebar';
import { GlobalButton } from '../components/GlobalButton';
import GlobalCardRadioButton from '../components/GlobalCardRadioButton';
import { GlobalCheckbox } from '../components/GlobalCheckbox';
import { GlobalDropdown } from '../components/GlobalDropdown';
import { GlobalInput } from '../components/GlobalInput';
import { GlobalTextarea } from '../components/GlobalTextarea';
import { OpportunityChatPanel } from '../components/OpportunityChatPanel';
import { OpportunityPnlPanel } from '../components/OpportunityPnlPanel';
import { OpportunityScoreTrend } from '../components/OpportunityScoreTrend';
import { MarkdownContent } from '../components/MarkdownContent';
import { BusinessOpportunityForm } from '../components/BusinessOpportunityForm';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import GlobalTabs from '../components/GlobalTabs';
import { useAnalysisJobs } from '../context/AnalysisJobsContext';
import { useAppData } from '../context/AppDataContext';
import { useOpportunityHardening } from '../context/OpportunityHardeningContext';
import { useOpportunityPnlGeneration } from '../context/OpportunityPnlGenerationContext';
import { useOpportunityRoadmapGeneration } from '../context/OpportunityRoadmapGenerationContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { db } from '../lib/firebase';
import { COLLECTION_RAG_CHUNKS, ownedItemDocId } from '../lib/firestorePaths';
import {
  proposeOpportunityFixes,
  proposeOpportunityFixesErrorMessage,
  type OpportunityFixProposal,
} from '../lib/proposeOpportunityFixes';
import {
  refineBusinessOpportunity,
  refineBusinessOpportunityErrorMessage,
} from '../lib/refineBusinessOpportunity';
import {
  deleteBusinessOpportunity,
  saveBusinessOpportunity,
} from '../lib/saveBusinessOpportunity';
import type {
  BusinessOpportunity,
  InvestmentCase,
  OpportunityFeedback,
  OpportunityFeedbackSentiment,
  OpportunityRefinement,
  OpportunityRoadmapItem,
  OpportunityRoadmapLevel,
  OpportunityRoadmapPhase,
} from '../types';
import { compileOpportunityBrief } from '../utils/businessOpportunity';
import { deckIntelligenceSourceFingerprint } from '../utils/deckIntelligence';
import { buildOpportunityIteration } from '../utils/opportunityIterations';
import {
  deriveOpportunityWeakPoints,
  weakPointCategoryLabel,
  type OpportunityWeakPoint,
} from '../utils/opportunityWeakPoints';
import { formatDateLong } from '../utils/formatters';
import { newId } from '../utils/id';
import styles from './BusinessOpportunityDetailView.module.css';

type RagIndexStatus = 'loading' | 'indexed' | 'stale' | 'missing';

const DETAIL_TAB_KEYS = ['idea', 'analysis', 'refinery', 'roadmap', 'feedback', 'pnl', 'chat'] as const;
type DetailTabKey = (typeof DETAIL_TAB_KEYS)[number];

const MIN_IDEA_CHARS = 100;

const ROADMAP_PHASES: Array<{ key: OpportunityRoadmapPhase; label: string; hint: string }> = [
  { key: 'now', label: 'Now', hint: 'First build — the shippable wedge.' },
  { key: 'next', label: 'Next', hint: 'Once the wedge is in real hands.' },
  { key: 'later', label: 'Later', hint: 'Expansion once the core is proven.' },
];

const ROADMAP_LEVEL_OPTIONS: Array<{ value: OpportunityRoadmapLevel; label: string }> = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function roadmapLevelLabel(level: OpportunityRoadmapLevel): string {
  return level.charAt(0).toUpperCase() + level.slice(1);
}

function tabIndexFromSearch(tab: string | null): number {
  if (!tab) return 0;
  const i = DETAIL_TAB_KEYS.indexOf(tab as DetailTabKey);
  return i >= 0 ? i : 0;
}

export function BusinessOpportunityDetailView() {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { businessOpportunities, setInvestmentCases } = useAppData();
  const { isGenerating, getJobError, startPnlGeneration } = useOpportunityPnlGeneration();
  const {
    isGenerating: isGeneratingRoadmap,
    getJobError: getRoadmapJobError,
    startRoadmapGeneration,
  } = useOpportunityRoadmapGeneration();
  const {
    isHardening,
    getJobError: getHardeningJobError,
    startResolution,
  } = useOpportunityHardening();
  const {
    isAnalyzing,
    getJobError: getAnalysisJobError,
    startIdeaAnalysis,
  } = useAnalysisJobs();

  const [editOpen, setEditOpen] = useState(false);
  const [refinerySidebarOpen, setRefinerySidebarOpen] = useState(false);
  const [feedbackSidebarOpen, setFeedbackSidebarOpen] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState<string | null>(null);
  const [refinementDraft, setRefinementDraft] = useState('');
  const [fixTarget, setFixTarget] = useState<OpportunityWeakPoint | null>(null);
  const [fixProposals, setFixProposals] = useState<OpportunityFixProposal[] | null>(null);
  const [fixProposalsLoading, setFixProposalsLoading] = useState(false);
  const [fixProposalsError, setFixProposalsError] = useState<string | null>(null);
  const [selectedFixIndex, setSelectedFixIndex] = useState<number | 'custom' | null>(null);
  const [resolutionDraft, setResolutionDraft] = useState('');
  const [feedbackSource, setFeedbackSource] = useState('');
  const [feedbackQuote, setFeedbackQuote] = useState('');
  const [feedbackSentiment, setFeedbackSentiment] = useState<OpportunityFeedbackSentiment>('neutral');
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [roadmapEditTarget, setRoadmapEditTarget] = useState<OpportunityRoadmapItem | null>(null);
  const [roadmapItemDraft, setRoadmapItemDraft] = useState({
    title: '',
    description: '',
    phase: 'now' as OpportunityRoadmapPhase,
    impact: 'medium' as OpportunityRoadmapLevel,
    effort: 'medium' as OpportunityRoadmapLevel,
    timeframe: '',
  });
  const [roadmapEditError, setRoadmapEditError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [ragIndexStatus, setRagIndexStatus] = useState<RagIndexStatus>('loading');

  const opportunity = useMemo(
    () => (businessOpportunities ?? []).find((o) => o.id === opportunityId),
    [businessOpportunities, opportunityId]
  );

  const detailTabIndex = tabIndexFromSearch(searchParams.get('tab'));

  useEffect(() => {
    if (!userId || !opportunityId) {
      setRagIndexStatus('missing');
      return;
    }

    setRagIndexStatus('loading');
    const chunkDocRef = doc(
      db,
      COLLECTION_RAG_CHUNKS,
      ownedItemDocId(userId, `bo-${opportunityId}`)
    );

    return onSnapshot(
      chunkDocRef,
      (snap) => {
        if (!snap.exists()) {
          setRagIndexStatus('missing');
          return;
        }

        const chunkUpdatedAt = snap.get('updatedAt');
        const opportunityUpdatedAt = opportunity?.updatedAt;
        if (
          typeof chunkUpdatedAt === 'string' &&
          typeof opportunityUpdatedAt === 'string' &&
          chunkUpdatedAt >= opportunityUpdatedAt
        ) {
          setRagIndexStatus('indexed');
          return;
        }

        setRagIndexStatus('stale');
      },
      () => setRagIndexStatus('missing')
    );
  }, [userId, opportunityId, opportunity?.updatedAt]);

  function setDetailTabIndex(index: number) {
    const key = DETAIL_TAB_KEYS[index] ?? 'idea';
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true }
    );
  }

  async function persistOpportunity(next: BusinessOpportunity) {
    await saveBusinessOpportunity(userId, {
      id: next.id,
      name: next.name,
      industry: next.industry,
      ideaSummary: next.ideaSummary,
      status: next.status,
      ideaIntelligence: next.ideaIntelligence,
      refinements: next.refinements,
      iterations: next.iterations,
      feedback: next.feedback,
      pnl: next.pnl,
      roadmap: next.roadmap,
      promotedCaseId: next.promotedCaseId,
      existing: opportunity,
    });
  }

  if (!opportunityId || !opportunity) {
    return (
      <ViewPage title="Opportunities" backTo="/opportunities" backLabel="Back to opportunities">
        <section>
          <p className="muted">Opportunity not found.</p>
        </section>
      </ViewPage>
    );
  }

  const o = opportunity;
  const analyzing = isAnalyzing('idea', o.id);
  const analyzeError = getAnalysisJobError('idea', o.id);
  const generatingPnl = isGenerating(o.id);
  const pnlError = getJobError(o.id);
  const hardening = isHardening(o.id);
  const hardeningError = getHardeningJobError(o.id);
  const generatingRoadmap = isGeneratingRoadmap(o.id);
  const roadmapError = getRoadmapJobError(o.id);
  const weakPoints = o.ideaIntelligence ? deriveOpportunityWeakPoints(o.ideaIntelligence) : [];
  const ideaChars = o.ideaSummary.trim().length;
  const canAnalyze = ideaChars >= MIN_IDEA_CHARS;
  const fingerprint = deckIntelligenceSourceFingerprint(o.ideaSummary.trim());
  const analysisStale =
    Boolean(o.ideaIntelligence) && o.ideaIntelligence?.sourceTextFingerprint !== fingerprint;

  async function saveEdited(next: BusinessOpportunity) {
    try {
      await persistOpportunity(next);
      setEditOpen(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  async function deleteOpportunity() {
    if (!userId) return;
    await deleteBusinessOpportunity(userId, o.id);
    navigate('/opportunities');
  }

  function runAnalysis() {
    if (analyzing || !canAnalyze) return;
    void startIdeaAnalysis(o.id);
  }

  async function runRefine(e: FormEvent) {
    e.preventDefault();
    const note = refinementDraft.trim();
    if (!note || refining) return;

    setRefining(true);
    setRefineError(null);

    const userRefinement: OpportunityRefinement = {
      id: newId(),
      createdAt: new Date().toISOString(),
      type: 'user',
      content: note,
    };

    try {
      const result = await refineBusinessOpportunity(o.id, note);
      const aiRefinement: OpportunityRefinement = {
        id: newId(),
        createdAt: new Date().toISOString(),
        type: 'ai',
        content: result.aiRefinement,
      };
      const iteration = buildOpportunityIteration(
        'refine',
        result.aiRefinement,
        result.ideaIntelligence
      );
      const next: BusinessOpportunity = {
        ...o,
        ideaIntelligence: result.ideaIntelligence,
        refinements: [...(o.refinements ?? []), userRefinement, aiRefinement],
        iterations: [...(o.iterations ?? []), iteration],
        status: o.status === 'draft' ? 'refining' : o.status,
        updatedAt: new Date().toISOString(),
      };
      await persistOpportunity(next);
      setRefinementDraft('');
      setRefinerySidebarOpen(false);
    } catch (err) {
      setRefineError(refineBusinessOpportunityErrorMessage(err));
    } finally {
      setRefining(false);
    }
  }

  async function addFeedback(e: FormEvent) {
    e.preventDefault();
    const source = feedbackSource.trim();
    const quote = feedbackQuote.trim();
    if (!source || !quote) return;

    setFeedbackError(null);

    const entry: OpportunityFeedback = {
      id: newId(),
      createdAt: new Date().toISOString(),
      source,
      quote,
      sentiment: feedbackSentiment,
    };

    try {
      await persistOpportunity({
        ...o,
        feedback: [...(o.feedback ?? []), entry],
        updatedAt: new Date().toISOString(),
      });
      setFeedbackSource('');
      setFeedbackQuote('');
      setFeedbackSentiment('neutral');
      setFeedbackSidebarOpen(false);
    } catch (err) {
      setFeedbackError(err instanceof Error ? err.message : 'Could not save feedback.');
    }
  }

  function closeRefinerySidebar() {
    setRefinerySidebarOpen(false);
    setRefineError(null);
    setRefinementDraft('');
  }

  function openFixSidebar(point: OpportunityWeakPoint) {
    setFixTarget(point);
    setFixProposals(null);
    setFixProposalsError(null);
    setSelectedFixIndex(null);
    setResolutionDraft('');
    setFixProposalsLoading(true);
    proposeOpportunityFixes(o.id, {
      category: point.category,
      title: point.title,
      detail: point.detail,
    })
      .then((result) => setFixProposals(result.proposals))
      .catch((err) => setFixProposalsError(proposeOpportunityFixesErrorMessage(err)))
      .finally(() => setFixProposalsLoading(false));
  }

  function closeFixSidebar() {
    setFixTarget(null);
    setFixProposals(null);
    setFixProposalsError(null);
    setSelectedFixIndex(null);
    setResolutionDraft('');
  }

  function selectFixProposal(index: number | 'custom') {
    setSelectedFixIndex(index);
    if (index === 'custom') {
      setResolutionDraft('');
      return;
    }
    const proposal = fixProposals?.[index];
    if (proposal) {
      setResolutionDraft(`${proposal.title}: ${proposal.description}`);
    }
  }

  function applyFix(e: FormEvent) {
    e.preventDefault();
    if (!fixTarget || hardening) return;
    const resolution = resolutionDraft.trim();
    if (resolution.length < 10) return;
    void startResolution(
      o.id,
      { category: fixTarget.category, title: fixTarget.title, detail: fixTarget.detail },
      resolution
    );
    closeFixSidebar();
  }

  function closeFeedbackSidebar() {
    setFeedbackSidebarOpen(false);
    setFeedbackError(null);
    setFeedbackSource('');
    setFeedbackQuote('');
    setFeedbackSentiment('neutral');
  }

  function openRoadmapItemEditor(item: OpportunityRoadmapItem) {
    setRoadmapEditTarget(item);
    setRoadmapItemDraft({
      title: item.title,
      description: item.description,
      phase: item.phase,
      impact: item.impact,
      effort: item.effort,
      timeframe: item.timeframe ?? '',
    });
    setRoadmapEditError(null);
  }

  function closeRoadmapItemEditor() {
    setRoadmapEditTarget(null);
    setRoadmapEditError(null);
  }

  async function persistRoadmapItems(items: OpportunityRoadmapItem[]) {
    if (!o.roadmap) return;
    await persistOpportunity({
      ...o,
      roadmap: { ...o.roadmap, items, editedAt: new Date().toISOString() },
      updatedAt: new Date().toISOString(),
    });
  }

  async function saveRoadmapItem(e: FormEvent) {
    e.preventDefault();
    if (!roadmapEditTarget || !o.roadmap) return;
    const title = roadmapItemDraft.title.trim();
    const description = roadmapItemDraft.description.trim();
    if (!title || !description) return;

    const items = o.roadmap.items.map((item) =>
      item.id === roadmapEditTarget.id
        ? {
            ...item,
            title,
            description,
            phase: roadmapItemDraft.phase,
            impact: roadmapItemDraft.impact,
            effort: roadmapItemDraft.effort,
            timeframe: roadmapItemDraft.timeframe.trim() || undefined,
          }
        : item
    );

    try {
      await persistRoadmapItems(items);
      closeRoadmapItemEditor();
    } catch (err) {
      setRoadmapEditError(err instanceof Error ? err.message : 'Could not save the roadmap item.');
    }
  }

  async function deleteRoadmapItem() {
    if (!roadmapEditTarget || !o.roadmap) return;
    const items = o.roadmap.items.filter((item) => item.id !== roadmapEditTarget.id);
    try {
      await persistRoadmapItems(items);
      closeRoadmapItemEditor();
    } catch (err) {
      setRoadmapEditError(err instanceof Error ? err.message : 'Could not delete the roadmap item.');
    }
  }

  async function toggleRoadmapItemDone(target: OpportunityRoadmapItem) {
    if (!o.roadmap) return;
    const items = o.roadmap.items.map((item) =>
      item.id === target.id ? { ...item, done: !item.done } : item
    );
    try {
      await persistRoadmapItems(items);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not update the roadmap item.');
    }
  }

  async function promoteToDealflow() {
    if (promoting || o.promotedCaseId) return;
    setPromoting(true);
    setActionError(null);

    try {
      const caseId = newId();
      const now = new Date().toISOString();
      const deckOcrText = compileOpportunityBrief(o);
      const investmentCase: InvestmentCase = {
        id: caseId,
        name: o.name,
        industry: o.industry ?? '',
        founderAmbition: 3,
        timeToRevenue: 3,
        exitExperience: 3,
        founderSkillset: 3,
        marketSize: 3,
        deckOcrText,
        createdAt: now,
        updatedAt: now,
      };

      setInvestmentCases((prev) => [...prev, investmentCase]);
      await persistOpportunity({
        ...o,
        promotedCaseId: caseId,
        status: o.status === 'draft' || o.status === 'refining' ? 'validated' : o.status,
        updatedAt: now,
      });
      navigate(`/investment-cases/${caseId}`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not promote to dealflow.');
    } finally {
      setPromoting(false);
    }
  }

  const pencilIcon = (
    <img
      src={isDarkMode ? '/assets/icons/pencil.svg' : '/assets/icons/pencil.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const promoteIcon = (
    <img
      src={isDarkMode ? '/assets/icons/arrow.up.forward.white.svg' : '/assets/icons/arrow.up.forward.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const plusIcon = (
    <img
      src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  const intelligenceIcon = (
    <img
      src={
        isDarkMode
          ? '/assets/icons/apple.intelligence.svg'
          : '/assets/icons/apple.intelligence.green.svg'
      }
      alt=""
      width={18}
      height={18}
    />
  );

  const headerActions = (
    <>
      {o.promotedCaseId ? (
        <GlobalButton
          type="button"
          variant="secondary"
          onClick={() => navigate(`/investment-cases/${o.promotedCaseId}`)}
        >
          View dealflow case
        </GlobalButton>
      ) : o.ideaIntelligence ? (
        <GlobalButton
          type="button"
          variant="secondary"
          disabled={promoting}
          onClick={() => void promoteToDealflow()}
          icon={promoteIcon}
        >
          {promoting ? 'Promoting…' : 'Promote to dealflow'}
        </GlobalButton>
      ) : null}
      <GlobalButton
        type="button"
        variant="secondary"
        onClick={() => setEditOpen(true)}
        icon={pencilIcon}
      >
        Edit
      </GlobalButton>
      <GlobalButton
        type="button"
        variant="secondary"
        destructive
        onClick={() => void deleteOpportunity()}
        icon={<img src="/assets/icons/trash.red.svg" alt="" width={16} height={16} />}
      >
        Delete
      </GlobalButton>
    </>
  );

  const indexBadge =
    ragIndexStatus === 'loading' ? null : ragIndexStatus === 'indexed' ? (
      <span className={styles.ideaIndexBadge}>Indexed for search</span>
    ) : ragIndexStatus === 'stale' ? (
      <span className={styles.ideaIndexBadgeStale}>Index outdated — re-index in Settings</span>
    ) : (
      <span className={styles.ideaIndexBadgeMissing}>Not indexed</span>
    );

  const ideaTab = (
    <>
      {actionError ? <p className="form-error">{actionError}</p> : null}
      <ViewSectionHeader
        eyebrow="Idea"
        title={o.name}
        subtitle={o.industry || undefined}
        actions={
          <GlobalButton
            type="button"
            variant="secondary"
            disabled={!canAnalyze || analyzing}
            onClick={() => runAnalysis()}
          >
            {analyzing ? 'Analyzing…' : o.ideaIntelligence ? 'Re-analyze idea' : 'Analyze idea'}
          </GlobalButton>
        }
      />

      {analyzeError ? <p className="form-error">{analyzeError}</p> : null}

      <div className={styles.ideaMeta}>
        <span
          className={`${styles.ideaCharCount}${!canAnalyze ? ` ${styles.ideaCharCountWarning}` : ''}`}
        >
          {ideaChars.toLocaleString()} characters
          {!canAnalyze ? ` · need ${MIN_IDEA_CHARS}+ to analyze` : ''}
        </span>
        {analysisStale ? (
          <span className={styles.ideaStalePill}>Idea changed — re-analyze</span>
        ) : null}
        {indexBadge}
      </div>

      <div className={styles.ideaBody}>
        <MarkdownContent content={o.ideaSummary} />
      </div>
    </>
  );

  const analysisTab = o.ideaIntelligence ? (
    <DeckIntelligenceSections intelligence={o.ideaIntelligence} />
  ) : (
    <GlobalEmptyState
      buttonText="Analyze idea"
      onButtonClick={() => {
        setDetailTabIndex(0);
      }}
    />
  );

  const refineryTab = (
    <>
      <ViewSectionHeader
        eyebrow="Refinery"
        title="Harden the idea"
        subtitle="Fix weak points one by one — each fix evolves the idea and re-runs the analysis."
        actions={
          <GlobalButton
            type="button"
            variant="secondary"
            onClick={() => setRefinerySidebarOpen(true)}
            icon={plusIcon}
          >
            Add refinement
          </GlobalButton>
        }
      />

      {hardeningError ? <p className="form-error">{hardeningError}</p> : null}

      <OpportunityScoreTrend iterations={o.iterations} />

      <section aria-label="Weak points">
        {!o.ideaIntelligence ? (
          <GlobalEmptyState
            buttonText="Analyze idea"
            onButtonClick={() => setDetailTabIndex(0)}
          />
        ) : weakPoints.length === 0 ? (
          <p className="muted">
            No open weak points — the analysis found nothing left to harden. Re-analyze after
            editing the idea to check again.
          </p>
        ) : (
          <ul className={styles.weakPointList}>
            {weakPoints.map((point) => (
              <li key={point.id} className={styles.weakPointCard}>
                <div className={styles.weakPointHeader}>
                  <span
                    className={`${styles.weakPointSeverity} ${
                      point.severity >= 8
                        ? styles.weakPointSeverityHigh
                        : point.severity >= 6
                          ? styles.weakPointSeverityMedium
                          : styles.weakPointSeverityLow
                    }`}
                  >
                    {weakPointCategoryLabel(point.category)} · {point.severity}/10
                  </span>
                  <GlobalButton
                    type="button"
                    variant="secondary"
                    disabled={hardening}
                    onClick={() => openFixSidebar(point)}
                  >
                    {hardening ? 'Fixing…' : 'Fix this'}
                  </GlobalButton>
                </div>
                <p className={styles.weakPointTitle}>{point.title}</p>
                {point.detail ? <p className={styles.weakPointDetail}>{point.detail}</p> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Refinement timeline" className={styles.refinementTimeline}>
        <h3 className={styles.timelineHeading}>Refinement timeline</h3>
        {(o.refinements ?? []).length === 0 ? (
          <p className="muted">No refinements yet — fixes and notes will show up here.</p>
        ) : (
          <ul className="form-stack">
            {(o.refinements ?? []).map((r) => (
              <li key={r.id}>
                <p className="muted">
                  {formatDateLong(r.createdAt)} · {r.type === 'ai' ? 'AI' : 'You'}
                </p>
                <p>{r.content}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );

  const feedbackTab = (
    <>
      <ViewSectionHeader
        eyebrow="Feedback"
        title="Early signals"
        subtitle="Log conversations and reactions."
        actions={
          <GlobalButton
            type="button"
            variant="secondary"
            onClick={() => setFeedbackSidebarOpen(true)}
            icon={plusIcon}
          >
            Add feedback
          </GlobalButton>
        }
      />
      <section aria-label="Feedback log">
        {(o.feedback ?? []).length === 0 ? (
          <GlobalEmptyState
            buttonText="Add feedback"
            onButtonClick={() => setFeedbackSidebarOpen(true)}
          />
        ) : (
          <ul className="form-stack">
            {(o.feedback ?? []).map((f) => (
              <li key={f.id}>
                <p className="muted">
                  {formatDateLong(f.createdAt)} · {f.source}
                  {f.sentiment ? ` · ${f.sentiment}` : ''}
                </p>
                <p>{f.quote}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );

  const roadmapTab = (
    <>
      <ViewSectionHeader
        eyebrow="Roadmap"
        title="Initial feature roadmap"
        subtitle={
          o.roadmap
            ? 'From first shippable wedge to a proven core — edit items to keep the plan honest.'
            : 'Generate a professionally written first roadmap — phased into now, next, and later, with impact and effort per feature.'
        }
        actions={
          <GlobalButton
            type="button"
            variant="primary"
            disabled={!canAnalyze || generatingRoadmap}
            onClick={() => void startRoadmapGeneration(o.id)}
          >
            {generatingRoadmap
              ? 'Generating…'
              : o.roadmap
                ? 'Regenerate roadmap'
                : 'Generate roadmap'}
          </GlobalButton>
        }
      />
      {roadmapError ? <p className="form-error">{roadmapError}</p> : null}
      {o.roadmap ? (
        <>
          <p className={styles.roadmapMeta}>
            Generated {formatDateLong(o.roadmap.generatedAt)}
            {o.roadmap.editedAt ? ` · edited ${formatDateLong(o.roadmap.editedAt)}` : ''}
          </p>
          {o.roadmap.summary ? <p className={styles.roadmapSummary}>{o.roadmap.summary}</p> : null}
          {ROADMAP_PHASES.map((phase) => {
            const items = o.roadmap!.items.filter((item) => item.phase === phase.key);
            if (items.length === 0) return null;
            return (
              <section key={phase.key} aria-label={`${phase.label} phase`} className={styles.roadmapPhase}>
                <h3 className={styles.roadmapPhaseHeading}>{phase.label}</h3>
                <p className={styles.roadmapPhaseHint}>{phase.hint}</p>
                <ul className={styles.roadmapList}>
                  {items.map((item) => (
                    <li
                      key={item.id}
                      className={`${styles.roadmapCard}${item.done ? ` ${styles.roadmapCardDone}` : ''}`}
                    >
                      <div className={styles.roadmapCardHeader}>
                        <div className={styles.roadmapPills}>
                          <span
                            className={
                              item.impact === 'high' ? styles.roadmapPillPositive : styles.roadmapPill
                            }
                          >
                            Impact · {roadmapLevelLabel(item.impact)}
                          </span>
                          <span
                            className={
                              item.effort === 'high' ? styles.roadmapPillWarning : styles.roadmapPill
                            }
                          >
                            Effort · {roadmapLevelLabel(item.effort)}
                          </span>
                          {item.timeframe ? (
                            <span className={styles.roadmapPill}>{item.timeframe}</span>
                          ) : null}
                        </div>
                        <div className={styles.roadmapCardActions}>
                          <GlobalCheckbox
                            label="Done"
                            checked={Boolean(item.done)}
                            onChange={() => void toggleRoadmapItemDone(item)}
                          />
                          <GlobalButton
                            type="button"
                            variant="secondary"
                            onClick={() => openRoadmapItemEditor(item)}
                            icon={pencilIcon}
                          >
                            Edit
                          </GlobalButton>
                        </div>
                      </div>
                      <p className={styles.roadmapTitle}>{item.title}</p>
                      <p className={styles.roadmapDescription}>{item.description}</p>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      ) : (
        <GlobalEmptyState
          buttonText="Generate roadmap"
          onButtonClick={() => void startRoadmapGeneration(o.id)}
        />
      )}
    </>
  );

  const pnlTab = (
    <>
      <ViewSectionHeader
        eyebrow="Financial model"
        title="P&L projection"
        subtitle={
          o.pnl
            ? `Month-by-month P&L for years 1–3 — last generated ${formatDateLong(o.pnl.generatedAt)}.`
            : 'Month-by-month P&L for years 1–3 — models the leanest credible path to profitability, not a burn-only scenario.'
        }
        actions={
          <GlobalButton
            type="button"
            variant="primary"
            disabled={!canAnalyze || generatingPnl}
            onClick={() => void startPnlGeneration(o.id)}
          >
            {generatingPnl ? 'Generating…' : o.pnl ? 'Regenerate P&L' : 'Generate P&L'}
          </GlobalButton>
        }
      />
      {pnlError ? <p className="form-error">{pnlError}</p> : null}
      {o.pnl ? (
        <OpportunityPnlPanel pnl={o.pnl} />
      ) : (
        <GlobalEmptyState
          buttonText="Generate P&L"
          onButtonClick={() => void startPnlGeneration(o.id)}
        />
      )}
    </>
  );

  const chatTab = (
    <>
      <ViewSectionHeader
        eyebrow="Chat"
        title="Talk it through"
        subtitle="Ask questions about this opportunity — grounded in the idea, its analysis, and your Perfina data. Conversations are saved as separate threads."
      />
      <OpportunityChatPanel userId={userId} opportunityId={o.id} opportunityName={o.name} />
    </>
  );

  return (
    <>
      <ViewPage
        title={o.name}
        backTo="/opportunities"
        backLabel="Back to opportunities"
        actions={headerActions}
      >
        <section>
          <GlobalTabs
            initialIndex={detailTabIndex}
            onChange={setDetailTabIndex}
            tabs={[
              { label: 'Idea', content: ideaTab },
              { label: 'Analysis', content: analysisTab },
              { label: 'Refinery', content: refineryTab },
              { label: 'Roadmap', content: roadmapTab },
              { label: 'Feedback', content: feedbackTab },
              { label: 'P&L', content: pnlTab },
              { label: 'Chat', content: chatTab },
            ]}
          />
        </section>
      </ViewPage>

      <GlobalSidebar isOpen={editOpen} onClose={() => setEditOpen(false)}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Edit opportunity</h2>
          <BusinessOpportunityForm
            opportunity={o}
            onSubmit={(next) => void saveEdited(next)}
            onCancel={() => setEditOpen(false)}
          />
        </div>
      </GlobalSidebar>

      <GlobalSidebar isOpen={refinerySidebarOpen} onClose={closeRefinerySidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Add refinement</h2>
          <form className="form-stack" onSubmit={(e) => void runRefine(e)}>
            <GlobalTextarea
              label="Refinement note"
              value={refinementDraft}
              onChange={(e) => setRefinementDraft(e.target.value)}
              placeholder="Challenge an assumption, add new evidence, or pivot the thesis…"
              rows={4}
            />
            {refineError ? <p className="form-error">{refineError}</p> : null}
            <div className="sidebarFormActions">
              <GlobalButton type="button" variant="secondary" disabled={refining} onClick={closeRefinerySidebar}>
                Cancel
              </GlobalButton>
              <GlobalButton
                type="submit"
                variant="primary"
                disabled={refining || refinementDraft.trim().length < 10}
              >
                {refining ? 'Re-forging…' : 'Re-forge analysis'}
              </GlobalButton>
            </div>
          </form>
        </div>
      </GlobalSidebar>

      <GlobalSidebar isOpen={fixTarget !== null} onClose={closeFixSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Fix weak point</h2>
          {fixTarget ? (
            <form className="form-stack" onSubmit={applyFix}>
              <div className={styles.fixTargetSummary}>
                <span className={styles.weakPointSeverity}>
                  {weakPointCategoryLabel(fixTarget.category)}
                </span>
                <p className={styles.weakPointTitle}>{fixTarget.title}</p>
                {fixTarget.detail ? (
                  <p className={styles.weakPointDetail}>{fixTarget.detail}</p>
                ) : null}
              </div>

              {fixProposalsLoading ? (
                <p className="muted">Generating fix proposals…</p>
              ) : fixProposalsError ? (
                <p className="form-error">{fixProposalsError}</p>
              ) : fixProposals ? (
                <div className={styles.fixProposalList}>
                  {fixProposals.map((proposal, index) => (
                    <GlobalCardRadioButton
                      key={`${proposal.title}-${index}`}
                      icon={intelligenceIcon}
                      headline={proposal.title}
                      paragraph={
                        proposal.expectedImpact
                          ? `${proposal.description} ${proposal.expectedImpact}`
                          : proposal.description
                      }
                      ctaLabel="Use this fix"
                      ctaIcon={null}
                      name="fixProposal"
                      value={String(index)}
                      isSelected={selectedFixIndex === index}
                      onSelect={() => selectFixProposal(index)}
                    />
                  ))}
                  <GlobalCardRadioButton
                    icon={pencilIcon}
                    headline="Write your own fix"
                    paragraph="Describe the change to the idea in your own words."
                    ctaLabel="Write fix"
                    ctaIcon={null}
                    name="fixProposal"
                    value="custom"
                    isSelected={selectedFixIndex === 'custom'}
                    onSelect={() => selectFixProposal('custom')}
                  />
                </div>
              ) : null}

              {selectedFixIndex !== null ? (
                <GlobalTextarea
                  label="Resolution to bake into the idea"
                  value={resolutionDraft}
                  onChange={(e) => setResolutionDraft(e.target.value)}
                  placeholder="e.g. Add a technical co-founder with a prior marketplace exit…"
                  rows={5}
                />
              ) : null}

              <div className="sidebarFormActions">
                <GlobalButton type="button" variant="secondary" onClick={closeFixSidebar}>
                  Cancel
                </GlobalButton>
                <GlobalButton
                  type="submit"
                  variant="primary"
                  disabled={hardening || resolutionDraft.trim().length < 10}
                >
                  Apply fix
                </GlobalButton>
              </div>
            </form>
          ) : null}
        </div>
      </GlobalSidebar>

      <GlobalSidebar isOpen={roadmapEditTarget !== null} onClose={closeRoadmapItemEditor}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Edit roadmap item</h2>
          {roadmapEditTarget ? (
            <form className="form-stack" onSubmit={(e) => void saveRoadmapItem(e)}>
              <GlobalInput
                label="Title"
                value={roadmapItemDraft.title}
                onChange={(e) =>
                  setRoadmapItemDraft((draft) => ({ ...draft, title: e.target.value }))
                }
              />
              <GlobalTextarea
                label="Description"
                value={roadmapItemDraft.description}
                onChange={(e) =>
                  setRoadmapItemDraft((draft) => ({ ...draft, description: e.target.value }))
                }
                rows={4}
              />
              <GlobalDropdown
                label="Phase"
                value={roadmapItemDraft.phase}
                onChange={(value) =>
                  setRoadmapItemDraft((draft) => ({
                    ...draft,
                    phase: value as OpportunityRoadmapPhase,
                  }))
                }
                options={ROADMAP_PHASES.map((phase) => ({ value: phase.key, label: phase.label }))}
              />
              <GlobalDropdown
                label="Impact"
                value={roadmapItemDraft.impact}
                onChange={(value) =>
                  setRoadmapItemDraft((draft) => ({
                    ...draft,
                    impact: value as OpportunityRoadmapLevel,
                  }))
                }
                options={ROADMAP_LEVEL_OPTIONS}
              />
              <GlobalDropdown
                label="Effort"
                value={roadmapItemDraft.effort}
                onChange={(value) =>
                  setRoadmapItemDraft((draft) => ({
                    ...draft,
                    effort: value as OpportunityRoadmapLevel,
                  }))
                }
                options={ROADMAP_LEVEL_OPTIONS}
              />
              <GlobalInput
                label="Timeframe"
                value={roadmapItemDraft.timeframe}
                onChange={(e) =>
                  setRoadmapItemDraft((draft) => ({ ...draft, timeframe: e.target.value }))
                }
                placeholder="e.g. Weeks 1-3"
              />
              {roadmapEditError ? <p className="form-error">{roadmapEditError}</p> : null}
              <div className="sidebarFormActions">
                <GlobalButton
                  type="button"
                  variant="secondary"
                  destructive
                  onClick={() => void deleteRoadmapItem()}
                >
                  Delete item
                </GlobalButton>
                <GlobalButton type="button" variant="secondary" onClick={closeRoadmapItemEditor}>
                  Cancel
                </GlobalButton>
                <GlobalButton
                  type="submit"
                  variant="primary"
                  disabled={
                    !roadmapItemDraft.title.trim() || !roadmapItemDraft.description.trim()
                  }
                >
                  Save item
                </GlobalButton>
              </div>
            </form>
          ) : null}
        </div>
      </GlobalSidebar>

      <GlobalSidebar isOpen={feedbackSidebarOpen} onClose={closeFeedbackSidebar}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Add feedback</h2>
          <form className="form-stack" onSubmit={(e) => void addFeedback(e)}>
            <GlobalInput
              label="Source"
              value={feedbackSource}
              onChange={(e) => setFeedbackSource(e.target.value)}
              placeholder="e.g. Potential customer, advisor"
            />
            <GlobalTextarea
              label="Quote or note"
              value={feedbackQuote}
              onChange={(e) => setFeedbackQuote(e.target.value)}
              rows={3}
            />
            <label className="form-label">
              Sentiment
              <select
                className="global-input"
                value={feedbackSentiment}
                onChange={(e) => setFeedbackSentiment(e.target.value as OpportunityFeedbackSentiment)}
              >
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
            </label>
            {feedbackError ? <p className="form-error">{feedbackError}</p> : null}
            <div className="sidebarFormActions">
              <GlobalButton type="button" variant="secondary" onClick={closeFeedbackSidebar}>
                Cancel
              </GlobalButton>
              <GlobalButton
                type="submit"
                variant="primary"
                disabled={!feedbackSource.trim() || !feedbackQuote.trim()}
              >
                Add feedback
              </GlobalButton>
            </div>
          </form>
        </div>
      </GlobalSidebar>
    </>
  );
}
